import { DecimalPipe } from '@angular/common';
import {
  AfterContentInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { PRODUCT_PLACEHOLDER } from 'src/app/app.settings';
import { Product, ProductComposition, ProductPackageOption } from 'src/app/models';
import { buttonIcons } from 'src/app/modules/icon.module';
import { BasketService } from 'src/app/services/basket/basket.service';
import { ProductService } from 'src/app/services/product';
import { ThemeService } from 'src/app/services/theme/theme.service';
import { IconButtonComponent } from 'src/app/shared/components';
import { CounterComponent } from 'src/app/shared/components/product/counter/counter.component';
import { environment } from 'src/environments/environment';
import { DisplayCurrencyPipe } from '../../../../../shared/pipes/display-currency.pipe';
import { ProductModulePipe } from '../../../../../shared/pipes/module.pipe';
import { PrimaryProductImagePipe } from '../../../../../shared/pipes/primary-product-image.pipe';

@Component({
  selector: 'app-meny-basket-item',
  templateUrl: './meny-basket-item.component.html',
  styleUrls: ['./meny-basket-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    RouterLink,
    PrimaryProductImagePipe,
    ProductModulePipe,
    DecimalPipe,
    DisplayCurrencyPipe,
    CounterComponent,
    IconButtonComponent,
  ],
})
export class MenyBasketItemComponent implements AfterContentInit {
  private basketService = inject(BasketService);
  private productService = inject(ProductService);
  cdr = inject(ChangeDetectorRef);
  private themeService = inject(ThemeService);
  private elRef = inject(ElementRef);

  @Input() product: ProductComposition | Product;
  @Input() quantity: number;
  @Input() error: boolean;
  @Output() callback = new EventEmitter();
  @Output() deleteCallback = new EventEmitter();

  locale = environment.i18n.language;
  currency = environment.i18n.currency;
  deleteLineIcon = buttonIcons.bigCross;
  placeholder = PRODUCT_PLACEHOLDER;

  ngAfterContentInit() {
    this.themeService.subscribeComponent(this.elRef);
  }

  addProduct(product: Product, quantity: number, packageOptions: ProductPackageOption[]) {
    this.callback.emit();
    this.basketService.addProduct(product, quantity, packageOptions);
  }

  removeProduct(product: Product, prevQuantity: number, quantity: number, packageOptions: ProductPackageOption[]) {
    this.callback.emit();
    quantity = quantity || 1;

    if (prevQuantity === quantity) {
      this.basketService.deleteProduct(product);
      this.deleteCallback.emit();
      return;
    }

    this.basketService.removeProduct(product, quantity, packageOptions);
  }

  deleteProduct(product: Product) {
    this.callback.emit();
    this.basketService.deleteProduct(product);
    this.deleteCallback.emit();
  }

  createProductUrl(product: Product) {
    return this.productService.createProductUrl(product);
  }

  calculatePrice(): number {
    let price = this.product.price;
    let moduleQuantity = 0;

    const moduleChoices = (this.product as ProductComposition)?.moduleChoices;
    if (moduleChoices) {
      Object.values(moduleChoices).forEach((module) => {
        Object.values(module.products).forEach((product) => {
          price += product.price * product.quantity;
          moduleQuantity += product.quantity;
        });
      });
    }

    let discountPercent = 0;
    if ((this.product as ProductComposition).discountLevels) {
      (this.product as ProductComposition).discountLevels.forEach((discountLevel) => {
        if (
          discountPercent < discountLevel.discountPercent &&
          (this.quantity >= discountLevel.minimumQuantity ||
            (discountLevel.includeModuleProducts && moduleQuantity >= discountLevel.minimumQuantity))
        )
          discountPercent = discountLevel.discountPercent;
      });
    }

    return price * this.quantity * (1 - discountPercent / 100);
  }
}
